import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  CheckCircle,
  HourglassEmpty,
  Error as ErrorIcon,
  Gavel as GavelIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as CourtIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useSidebar } from '../contexts/SidebarContext';
import ConsultationHistory from '../components/ConsultationHistory';
import DocumentHistory from '../components/DocumentHistory';
import Sidebar from '../components/Sidebar';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api';

interface ConversationDetail {
  id: string;
  user_id: string;
  category: string;
  original_question: string;
  status: string;
  final_response: string | null;
  confidence_level: string | null;
  model_used: string | null;
  processing_time: number | null;
  created_at: string;
  updated_at: string;
  messages: Array<{
    id: string;
    message_type: string;
    content: string;
    metadata: any;
    order_index: number;
    created_at: string;
  }>;
  sources: Array<{
    type: string;
    name: string;
    url: string | null;
    relevance: number | null;
  }>;
  metadata?: {
    total_results?: number;
    search_results?: any[];
    search_time_ms?: number;
  };
}

const ConsultationHistoryPage: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { collapsed } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [selectedConversationId, setSelectedConversationId] = useState<string | undefined>();
  const [activityTab, setActivityTab] = useState<'consultations' | 'documents'>('consultations');
  const [conversationDetail, setConversationDetail] = useState<ConversationDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    messages: true,
    sources: true
  });
  const [openCaseDetail, setOpenCaseDetail] = useState(false);
  const [selectedCase, setSelectedCase] = useState<any>(null);
  const [loadingCaseDetail, setLoadingCaseDetail] = useState(false);

  const handleSelectConversation = async (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/consultation/${conversationId}`);

      if (response.data.success) {
        setConversationDetail(response.data.conversation);
      } else {
        setError('Nepodařilo se načíst detail konzultace');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Pro zobrazení detailu se prosím přihlaste');
      } else if (err.response?.status === 404) {
        setError('Konzultace nebyla nalezena');
      } else {
        setError('Chyba při načítání detailu konzultace');
      }
      console.error('Failed to load conversation detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCaseClick = async (caseItem: any) => {
    setSelectedCase(caseItem);
    setOpenCaseDetail(true);
    setLoadingCaseDetail(true);

    try {
      const response = await api.get(
        API_ENDPOINTS.jurisprudence.getCase(caseItem.id),
        { timeout: 30000 }
      );

      if (response.data) {
        setSelectedCase({
          ...caseItem,
          ...response.data.case,
          fullDetail: {
            ai_analysis: response.data.ai_analysis,
            key_legal_points: response.data.key_legal_points,
            related_cases: response.data.related_cases,
            similar_cases_reasoning: response.data.similar_cases_reasoning
          }
        });
      }
    } catch (error) {
      console.error('Failed to load case details:', error);
    } finally {
      setLoadingCaseDetail(false);
    }
  };

  const handleCloseCaseDetail = () => {
    setOpenCaseDetail(false);
    setSelectedCase(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle fontSize="small" color="success" />;
      case 'awaiting_clarification':
      case 'pending':
        return <HourglassEmpty fontSize="small" color="warning" />;
      case 'failed':
        return <ErrorIcon fontSize="small" color="error" />;
      default:
        return <HourglassEmpty fontSize="small" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      completed: 'Dokončeno',
      awaiting_clarification: 'Čeká na upřesnění',
      clarification_received: 'Zpracování',
      pending: 'Čeká',
      processing: 'Zpracování',
      failed: 'Chyba'
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      housing: 'Bydlení',
      work: 'Práce',
      family: 'Rodina',
      accidents: 'Nehody',
      documents: 'Dokumenty',
      general: 'Obecné'
    };
    return labels[category] || category;
  };

  const getMessageTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      user_question: 'Váš dotaz',
      clarifying_questions: 'Doptávací otázky',
      user_answers: 'Vaše odpovědi',
      final_response: 'Odpověď AI asistenta'
    };
    return labels[type] || type;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ display: 'flex' }}>
        <Sidebar />
        <Box sx={{
          flex: 1,
          marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
          paddingTop: isMobile ? '64px' : 0,
          minHeight: '100vh',
          backgroundColor: '#F2F2F2',
          transition: 'margin-left 0.3s ease'
        }}>
          <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Alert severity="info">
              Pro zobrazení historie aktivit se prosím přihlaste.
            </Alert>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />

      <Box sx={{
        flex: 1,
        marginLeft: isMobile ? 0 : (collapsed ? '80px' : '280px'),
        paddingTop: isMobile ? '64px' : 0,
        minHeight: '100vh',
        backgroundColor: '#F2F2F2',
        transition: 'margin-left 0.3s ease'
      }}>
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
            Historie aktivit
          </Typography>

          {/* Top-level activity tabs */}
          <Box sx={{ mb: 3 }}>
            <Tabs
              value={activityTab}
              onChange={(_e, v) => setActivityTab(v)}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#424242',
                  minHeight: 40,
                  px: 2,
                  borderRadius: 1,
                  mr: 1
                },
                '& .MuiTab-root.Mui-selected': {
                  color: '#e72f0d',
                  backgroundColor: 'rgba(231, 47, 13, 0.10)'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#e72f0d',
                  height: 3,
                  borderRadius: 2
                }
              }}
            >
              <Tab label="Konzultace" value="consultations" />
              <Tab label="Dokumenty" value="documents" />
            </Tabs>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left sidebar - Activity list (full width for Documents tab) */}
            <Box sx={{ width: { xs: '100%', md: activityTab === 'documents' ? '100%' : '33%' } }}>
              {activityTab === 'consultations' ? (
                <ConsultationHistory
                  onSelectConversation={handleSelectConversation}
                  selectedConversationId={selectedConversationId}
                />
              ) : (
                <DocumentHistory />
              )}
            </Box>

        {/* Right panel - Detail area (hidden for Documents tab to allow full-width list) */}
        {activityTab !== 'documents' && (<Box sx={{ flex: 1 }}>
          {/* Removed redundant helper text panel for Documents tab */}
          <>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && !conversationDetail && activityTab === 'consultations' && (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body1" color="text.secondary">
                Vyberte konzultaci z levého seznamu pro zobrazení detailu
              </Typography>
            </Paper>
          )}

          {!loading && !error && activityTab === 'consultations' && conversationDetail && conversationDetail.category === 'jurisprudence' && (
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              {/* Research Results View */}
              <Box sx={{ p: 3, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GavelIcon color="primary" />
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    Právní výzkum
                  </Typography>
                </Box>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  Výsledky pro: "{conversationDetail.original_question}"
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Nalezeno ${conversationDetail.metadata?.total_results || conversationDetail.metadata?.search_results?.length || 0} rozhodnutí`}
                    size="small"
                    color="primary"
                  />
                  {conversationDetail.metadata?.search_time_ms && (
                    <Chip
                      label={`Čas: ${(conversationDetail.metadata.search_time_ms / 1000).toFixed(2)}s`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              {/* Results List */}
              <Box sx={{ p: 3 }}>
                {conversationDetail.metadata?.search_results && conversationDetail.metadata.search_results.length > 0 ? (
                  <Stack spacing={2}>
                    {conversationDetail.metadata.search_results.map((result: any, index: number) => (
                      <Card
                        key={result.id || index}
                        onClick={() => handleCaseClick(result)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.3s ease',
                          border: '1px solid #e0e0e0',
                          borderRadius: '12px'
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#1F1F1F' }}>
                                {result.case_number}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <GavelIcon sx={{ fontSize: 16, color: '#666' }} />
                                <Typography variant="body2" color="text.secondary">
                                  <strong>{result.court_name || result.court}</strong>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  • {result.decision_date || result.date || 'Neznámé datum'}
                                </Typography>
                              </Box>
                              <Typography variant="body1" sx={{ lineHeight: 1.6, color: '#444' }}>
                                {result.case_summary || result.case_title || result.summary}
                              </Typography>
                              {result.legal_area && (
                                <Chip
                                  label={result.legal_area}
                                  size="small"
                                  sx={{ mt: 1 }}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                            {result.relevance_score && (
                              <Box sx={{ ml: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label={`${Math.round(result.relevance_score * 100)}%`}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                    backgroundColor: result.relevance_score > 0.8 ? 'rgba(76, 175, 80, 0.15)' : result.relevance_score > 0.6 ? 'rgba(231, 47, 13, 0.15)' : 'rgba(31, 31, 31, 0.08)',
                                    color: result.relevance_score > 0.8 ? '#2e7d32' : result.relevance_score > 0.6 ? '#e72f0d' : '#1F1F1F',
                                    border: result.relevance_score > 0.8 ? '1px solid rgba(76, 175, 80, 0.3)' : result.relevance_score > 0.6 ? '1px solid rgba(231, 47, 13, 0.3)' : '1px solid rgba(31, 31, 31, 0.15)'
                                  }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  relevance
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <GavelIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                      Žádné výsledky výzkumu
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {!loading && !error && activityTab === 'consultations' && conversationDetail && conversationDetail.category !== 'jurisprudence' && (
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              {/* Header */}
              <Box sx={{ p: 3, bgcolor: 'background.default', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  {getStatusIcon(conversationDetail.status)}
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    {conversationDetail.original_question}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {conversationDetail.category && (
                    <Chip
                      label={getCategoryLabel(conversationDetail.category)}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  <Chip
                    label={getStatusLabel(conversationDetail.status)}
                    size="small"
                    color={conversationDetail.status === 'completed' ? 'success' : 'default'}
                  />
                  {conversationDetail.confidence_level && (
                    <Chip
                      label={`Spolehlivost: ${conversationDetail.confidence_level}`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    Vytvořeno: {formatDate(conversationDetail.created_at)}
                  </Typography>
                  {conversationDetail.model_used && (
                    <Typography variant="caption" color="text.secondary">
                      Model: {conversationDetail.model_used}
                    </Typography>
                  )}
                  {conversationDetail.processing_time && (
                    <Typography variant="caption" color="text.secondary">
                      Čas zpracování: {conversationDetail.processing_time.toFixed(2)}s
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Messages Section */}
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => toggleSection('messages')}
                >
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    Průběh konverzace ({conversationDetail.messages.length})
                  </Typography>
                  <IconButton size="small">
                    {expandedSections.messages ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                <Collapse in={expandedSections.messages}>
                  <Box sx={{ p: 3, pt: 0 }}>
                    {conversationDetail.messages.map((message, index) => (
                      <Box key={message.id} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {getMessageTypeLabel(message.message_type)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(message.created_at)}
                          </Typography>
                        </Box>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2,
                            bgcolor: message.message_type.includes('user') ? 'background.default' : 'background.paper',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          <Typography variant="body2">{message.content}</Typography>
                        </Paper>
                        {index < conversationDetail.messages.length - 1 && <Divider sx={{ mt: 3 }} />}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>

              {/* Sources Section */}
              {conversationDetail.sources.length > 0 && (
                <Box>
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => toggleSection('sources')}
                  >
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                      Zdroje ({conversationDetail.sources.length})
                    </Typography>
                    <IconButton size="small">
                      {expandedSections.sources ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedSections.sources}>
                    <Box sx={{ p: 3, pt: 0 }}>
                      {conversationDetail.sources.map((source, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {source.name}
                          </Typography>
                          {source.url && (
                            <Typography
                              variant="body2"
                              component="a"
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                              {source.url}
                            </Typography>
                          )}
                          {source.relevance && (
                            <Typography variant="caption" color="text.secondary">
                              Relevance: {(source.relevance * 100).toFixed(0)}%
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Paper>
          )}
          </>
        </Box>
        )}
      </Box>

        {/* Case Detail Dialog */}
        <Dialog
        open={openCaseDetail}
        onClose={handleCloseCaseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <GavelIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Detail soudního rozhodnutí
            </Typography>
          </Box>
          <IconButton onClick={handleCloseCaseDetail} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {loadingCaseDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedCase ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Case Header */}
              <Box>
                <Typography variant="h5" gutterBottom sx={{ color: '#1F1F1F', fontWeight: 600 }}>
                  {selectedCase.case_number}
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ color: '#666' }}>
                  {selectedCase.case_title}
                </Typography>
              </Box>

              <Divider />

              {/* Case Info */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CourtIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Soud</Typography>
                    <Typography variant="body2">{selectedCase.court_name || selectedCase.court}</Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Datum rozhodnutí</Typography>
                    <Typography variant="body2">
                      {selectedCase.decision_date ?
                        new Date(selectedCase.decision_date).toLocaleDateString('cs-CZ') :
                        selectedCase.date || 'Neuvedeno'}
                    </Typography>
                  </Box>
                </Box>

                {selectedCase.legal_area && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Právní oblast</Typography>
                      <Typography variant="body2">{selectedCase.legal_area}</Typography>
                    </Box>
                  </Box>
                )}

                {selectedCase.court_type && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GavelIcon color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Typ soudu</Typography>
                      <Typography variant="body2">{selectedCase.court_type}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Summary */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                  Shrnutí rozhodnutí
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.7, color: '#333' }}>
                  {selectedCase.case_summary || selectedCase.summary}
                </Typography>
              </Box>

              {/* Keywords */}
              {selectedCase.keywords && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Klíčová slova
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCase.keywords.split(',').map((keyword: string, index: number) => (
                      <Chip
                        key={index}
                        label={keyword.trim()}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: '#1F1F1F',
                          color: '#1F1F1F'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Full decision text */}
              {selectedCase.full_decision_text && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Úplný text rozhodnutí
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#f9f9f9', maxHeight: 300, overflowY: 'auto' }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                      {selectedCase.full_decision_text}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedCase.fullDetail && (
                <>
                  {selectedCase.fullDetail.ai_analysis && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Analýza relevance
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          {selectedCase.fullDetail.ai_analysis}
                        </Typography>
                      </Paper>
                    </Box>
                  )}

                  {selectedCase.fullDetail.key_legal_points && selectedCase.fullDetail.key_legal_points.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Klíčové právní body
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        {selectedCase.fullDetail.key_legal_points.map((point: string, index: number) => (
                          <Box component="li" key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2">{point}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {selectedCase.fullDetail.related_cases && selectedCase.fullDetail.related_cases.length > 0 && (
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Související případy
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {selectedCase.fullDetail.related_cases.map((relatedCase: any) => (
                          <Paper key={relatedCase.id} sx={{ p: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F1F1F' }}>
                              {relatedCase.case_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {relatedCase.case_title}
                            </Typography>
                          </Paper>
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          ) : (
            <Typography>Žádná data k zobrazení</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          {selectedCase && selectedCase.source_url && (
            <Button
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(selectedCase.source_url, '_blank')}
              variant="outlined"
              sx={{
                color: '#1F1F1F',
                borderColor: '#1F1F1F',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  borderColor: '#1F1F1F'
                }
              }}
            >
              Otevřít originál
            </Button>
          )}
          <Button
            onClick={handleCloseCaseDetail}
            variant="contained"
            sx={{
              backgroundColor: '#1F1F1F',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#e72f0d'
              }
            }}
          >
            Zavřít
          </Button>
        </DialogActions>
      </Dialog>
        </Container>
      </Box>
    </Box>
  );
};

export default ConsultationHistoryPage;
